import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, Circle, ArrowLeft, Certificate, PlayCircle } from "@phosphor-icons/react";
import Seo from "@/components/Seo";

export default function CoursePlayer() {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [enrollment, setEnrollment] = useState(null);
  const [activeLessonKey, setActiveLessonKey] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) { navigate("/login"); return; }
    if (user) {
      api.get("/enrollments").then(({ data }) => {
        const found = data.find(e => e.id === enrollmentId);
        if (!found) { navigate("/dashboard"); return; }
        setEnrollment(found);
        // Auto-select first incomplete lesson
        const curriculum = found.course.curriculum || [];
        outer: for (let i = 0; i < curriculum.length; i++) {
          for (let j = 0; j < curriculum[i].lessons.length; j++) {
            const k = `${i}.${j}`;
            if (!found.completed_lessons.includes(k)) { setActiveLessonKey(k); break outer; }
          }
        }
        if (!activeLessonKey && curriculum[0]?.lessons?.[0]) setActiveLessonKey("0.0");
        setLoading(false);
      }).catch(() => { navigate("/dashboard"); });
    }
    // eslint-disable-next-line
  }, [user, authLoading, enrollmentId]);

  if (loading || !enrollment) return <div className="container-x py-32 font-mono text-sm">Loading...</div>;

  const curriculum = enrollment.course.curriculum || [];
  const totalLessons = curriculum.reduce((s, m) => s + m.lessons.length, 0);

  const completeLesson = async (key) => {
    const completed = Array.from(new Set([...enrollment.completed_lessons, key]));
    const progress = Math.round((completed.length / totalLessons) * 100);
    const { data } = await api.post(`/enrollments/${enrollment.id}/progress`, { completed_lessons: completed, progress });
    setEnrollment({ ...enrollment, completed_lessons: completed, progress, ...(data.certificate_id ? { certificate_id: data.certificate_id, completed_at: data.completed_at } : {}) });
  };

  let activeLesson = null;
  let activeModIdx = 0;
  if (activeLessonKey) {
    const [mi, li] = activeLessonKey.split(".").map(Number);
    activeModIdx = mi;
    activeLesson = curriculum[mi]?.lessons[li];
  }

  return (
    <div data-testid="course-player-page" className="bg-[#0A0A0A] min-h-screen">
      <Seo title="Course Player" path="/learn" noindex />
      <div className="border-b border-white/10">
        <div className="container-x py-6 flex items-center justify-between">
          <Link to="/dashboard" className="btn-ghost" data-testid="back-to-dashboard"><ArrowLeft size={14} /> Dashboard</Link>
          <div className="overline">{enrollment.course.code}</div>
        </div>
      </div>

      <div className="container-x py-12 grid grid-cols-12 gap-8">
        {/* Sidebar */}
        <aside className="col-span-12 lg:col-span-4">
          <h2 className="font-heading text-2xl tracking-tight mb-2">{enrollment.course.title}</h2>
          <div className="mb-6">
            <div className="flex justify-between text-xs font-mono mb-1.5">
              <span className="text-neutral-500">PROGRESS</span>
              <span className="text-[#FFD300]">{enrollment.progress}% · {enrollment.completed_lessons.length}/{totalLessons}</span>
            </div>
            <div className="h-1 bg-white/10 overflow-hidden">
              <div className="h-full bg-[#FFD300] transition-all" style={{ width: `${enrollment.progress}%` }} />
            </div>
          </div>

          {enrollment.certificate_id && (
            <div className="surface-card p-5 mb-6" data-testid="certificate-card">
              <div className="flex items-center gap-3 mb-2">
                <Certificate size={24} weight="duotone" className="text-[#F5D033]" />
                <div className="overline">Certificate Issued</div>
              </div>
              <div className="font-mono text-xs text-neutral-300 mb-1">ID: {enrollment.certificate_id}</div>
              <div className="font-mono text-xs text-neutral-500">{enrollment.course.certification}</div>
            </div>
          )}

          <div className="space-y-4" data-testid="lesson-list">
            {curriculum.map((mod, i) => (
              <div key={i}>
                <div className="overline mb-2">M{String(i+1).padStart(2,'0')} · {mod.module}</div>
                <ul className="space-y-px bg-white/5">
                  {mod.lessons.map((l, j) => {
                    const k = `${i}.${j}`;
                    const done = enrollment.completed_lessons.includes(k);
                    const active = activeLessonKey === k;
                    return (
                      <li key={j}>
                        <button
                          onClick={() => setActiveLessonKey(k)}
                          data-testid={`lesson-${k}`}
                          className={`w-full text-left p-3 flex items-center gap-3 transition-colors ${active ? 'bg-[#FFD300]/15 border-l-2 border-[#FFD300]' : 'bg-[#0A0A0A] hover:bg-white/5'}`}
                        >
                          {done ? <CheckCircle size={16} weight="duotone" className="text-[#039855]" /> : <Circle size={16} className="text-neutral-600" />}
                          <span className="flex-1 text-sm">{l.title}</span>
                          <span className="font-mono text-xs text-neutral-500">{l.duration}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </aside>

        {/* Player */}
        <main className="col-span-12 lg:col-span-8">
          {activeLesson ? (
            <div data-testid="active-lesson">
              <div className="overline mb-3">Module {activeModIdx+1} — Lesson</div>
              <h1 className="font-heading text-4xl tracking-tighter mb-6">{activeLesson.title}</h1>
              <div className="aspect-video bg-black border border-white/10 flex items-center justify-center mb-8 relative overflow-hidden">
                <img src={enrollment.course.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
                <div className="relative text-center">
                  <PlayCircle size={64} weight="duotone" className="text-[#FFD300] mx-auto mb-3" />
                  <div className="font-mono text-xs text-neutral-400 tracking-[0.2em] uppercase">Lesson Content</div>
                  <div className="font-mono text-xs text-neutral-600 mt-1">Duration: {activeLesson.duration}</div>
                </div>
              </div>

              <div className="surface-card p-6 mb-6">
                <div className="overline mb-3">// LESSON BRIEF</div>
                <p className="text-neutral-300 leading-relaxed">
                  This lesson covers <strong>{activeLesson.title}</strong>. Complete the lesson, then mark as
                  done to advance. Full course materials are delivered through the academy's secure portal
                  with live instruction available on scheduled training dates.
                </p>
              </div>

              <button
                onClick={() => completeLesson(activeLessonKey)}
                disabled={enrollment.completed_lessons.includes(activeLessonKey)}
                data-testid="complete-lesson-btn"
                className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {enrollment.completed_lessons.includes(activeLessonKey) ? "Lesson Completed ✓" : "Mark as Complete"}
              </button>
            </div>
          ) : (
            <div className="font-mono text-sm text-neutral-500">Select a lesson to begin.</div>
          )}
        </main>
      </div>
    </div>
  );
}
